/** @format */

import { useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { Radio, Toggle } from 'src/components';
import { TopicSearchAutocomplete } from 'src/components/search';
import { applyTopicToggle } from 'src/utils/contentEditor/applyTopicToggle';
import TopicSelectModal from './TopicSelectModal';

const NewsletterSection = ({
  t,
  newsletterActive,
  newsletterFrequency,
  newsletterTopics,
  newsletterTopicTree,
  newsletterTopicsLoading,
  newsletterTopicsError,
  onNewsletterActiveChange,
  onNewsletterFrequencyChange,
  onNewsletterTopicsChange
}) => {
  const [newsletterModalOpen, setNewsletterModalOpen] = useState(false);

  const newsletterFrequencyOptions = useMemo(
    () => [
      { id: 'monthly', label: t('personalData.newsletterFrequencyMonthly') },
      { id: 'weekly', label: t('personalData.newsletterFrequencyWeekly') },
      { id: 'daily', label: t('personalData.newsletterFrequencyDaily') }
    ],
    [t]
  );
  const safeNewsletterTopicTree = useMemo(
    () => (Array.isArray(newsletterTopicTree) ? newsletterTopicTree : []),
    [newsletterTopicTree]
  );
  const safeNewsletterTopics = useMemo(
    () => (Array.isArray(newsletterTopics) ? newsletterTopics : []),
    [newsletterTopics]
  );

  const onNewsletterToggle = event => {
    onNewsletterActiveChange(event.target.checked);
  };

  const onFrequencyChange = event => {
    onNewsletterFrequencyChange(event.target.value);
  };

  const onToggleTopic = topicId => {
    onNewsletterTopicsChange(applyTopicToggle(safeNewsletterTopics, safeNewsletterTopicTree, topicId));
  };

  const addTopicFromSuggestionOnly = topicId => {
    if (safeNewsletterTopics.some(id => String(id) === String(topicId))) return;
    onNewsletterTopicsChange([...safeNewsletterTopics, topicId]);
  };

  return (
    <>
      <Grid
        className={`personal-data__card personal-data__card--newsletter ${newsletterActive ? 'personal-data__card--newsletter-active' : ''}`}>
        <h4 className="sub_head">{t('personalData.newsletterHeading')}</h4>
        <Grid className="personal-data__card-head">
          <Toggle
            name="newsletter"
            checked={newsletterActive}
            onChange={onNewsletterToggle}
            label={t('personalData.newsletter')}
            id="newsletter"
          />
          <div
            className={`personal-data__newsletter-settings ${newsletterActive ? 'personal-data__newsletter-settings--active' : ''}`}>
            <div className="personal-data__newsletter-group">
              <div className="personal-data__newsletter-label">{t('personalData.newsletterFrequency')}</div>
              <div className="personal-data__newsletter-frequency-options">
                {newsletterFrequencyOptions.map(option => (
                  <Radio
                    key={option.id}
                    id={`newsletter-frequency-${option.id}`}
                    name="newsletterFrequency"
                    value={option.id}
                    checked={newsletterFrequency === option.id}
                    onChange={onFrequencyChange}
                    label={option.label}
                    className="personal-data__newsletter-frequency-option"
                  />
                ))}
              </div>
            </div>
            <div className="personal-data__newsletter-group">
              <div className="personal-data__newsletter-label">{t('personalData.newsletterInterests')}</div>
              <TopicSearchAutocomplete
                variant="newsletter"
                idPrefix="newsletter-topic"
                topicTree={safeNewsletterTopicTree}
                selectedTopicIds={safeNewsletterTopics}
                onToggleTopic={onToggleTopic}
                onAddTopicFromSearch={addTopicFromSuggestionOnly}
                loading={newsletterTopicsLoading}
                loadingMessage={t('extranet.loadingTopics')}
                placeholder={t('contentEditor.topicSearchPlaceholder')}
                inputAriaLabel={t('common.topic')}
                listAriaLabel={t('contentEditor.topicSuggestionsAria')}
                selectedBadgeText={t('contentEditor.topicSuggestionSelected')}
                getRemoveChipAriaLabel={name => t('common.removeItemAria', { name })}
                trailingSlot={
                  <button
                    type="button"
                    className="personal-data__newsletter-topics-btn"
                    onClick={() => setNewsletterModalOpen(true)}>
                    {t('personalData.newsletterSeeAllTopics')}
                  </button>
                }
              />
            </div>
          </div>
        </Grid>
      </Grid>
      <TopicSelectModal
        open={newsletterModalOpen}
        onClose={() => setNewsletterModalOpen(false)}
        topicTree={safeNewsletterTopicTree}
        selectedTopicIds={safeNewsletterTopics}
        onToggleTopic={onToggleTopic}
        loading={newsletterTopicsLoading}
        error={newsletterTopicsError}
      />
    </>
  );
};

export default NewsletterSection;
